/**
 * Impersonation Store - Manages impersonation state when app is running in simulator iframe
 *
 * This store handles:
 * - Detecting impersonation token from URL
 * - Validating the token with the backend
 * - Storing the impersonation state
 * - Providing the token for API requests
 */

import { create } from 'zustand';
import { api } from '../lib/api';

interface ImpersonationState {
  // State
  isImpersonated: boolean;
  isValidating: boolean;
  token: string | null;
  adminId: string | null;
  adminName: string | null;
  expiresAt: Date | null;
  targetUser: {
    id: string;
    name: string;
    email: string;
    role: string;
  } | null;

  // Actions
  initFromUrl: () => Promise<void>;
  setToken: (token: string) => void;
  clearImpersonation: () => void;
  getToken: () => string | null;
}

export const useImpersonationStore = create<ImpersonationState>((set, get) => ({
  isImpersonated: false,
  isValidating: false,
  token: null,
  adminId: null,
  adminName: null,
  expiresAt: null,
  targetUser: null,

  /**
   * Initialize impersonation from URL parameter
   * Called on app startup to detect if running in simulator iframe
   */
  initFromUrl: async () => {
    // Check for impersonation token in URL
    const params = new URLSearchParams(window.location.search);
    const impersonateToken = params.get('_impersonate');

    if (!impersonateToken) {
      return;
    }

    // Remove token from URL for security
    const url = new URL(window.location.href);
    url.searchParams.delete('_impersonate');
    window.history.replaceState({}, '', url.toString());

    set({ isValidating: true });

    try {
      // Validate token with backend
      const response = await api.impersonation.validateToken(impersonateToken);

      if (response.success && response.data?.valid) {
        const { adminId, adminName, expiresAt, targetUser } = response.data;

        set({
          isImpersonated: true,
          token: impersonateToken,
          adminId: adminId || null,
          adminName: adminName || null,
          expiresAt: expiresAt ? new Date(expiresAt) : null,
          targetUser: targetUser ? {
            id: targetUser.id,
            name: targetUser.name,
            email: targetUser.email,
            role: targetUser.role,
          } : null,
          isValidating: false,
        });

        console.log('[Impersonation] Session active - viewing as', targetUser?.name);
      } else {
        console.warn('[Impersonation] Token validation failed:', response.data?.reason);
        set({ isValidating: false });
      }
    } catch (error) {
      console.error('[Impersonation] Failed to validate token:', error);
      set({ isValidating: false });
    }
  },

  /**
   * Manually set impersonation token (for testing)
   */
  setToken: (token: string) => {
    set({ token, isImpersonated: true });
  },

  /**
   * Clear impersonation state
   */
  clearImpersonation: () => {
    set({
      isImpersonated: false,
      token: null,
      adminId: null,
      adminName: null,
      expiresAt: null,
      targetUser: null,
    });
  },

  /**
   * Get the current impersonation token (for API requests)
   */
  getToken: () => {
    return get().token;
  },
}));

/**
 * Get impersonation token for API client
 * Used by the fetchApi function to add X-Impersonation-Token header
 */
export function getImpersonationToken(): string | null {
  return useImpersonationStore.getState().token;
}
